/**
 * Centralized locale-aware formatting utilities.
 * All money, date, and phone formatting should go through these functions
 * instead of inline Intl.NumberFormat calls.
 */

import { getCountryConfig } from "./countries";

// ─── Money Formatting ────────────────────────────────────────────────────────

const moneyFormatterCache = new Map<string, Intl.NumberFormat>();

function getMoneyFormatter(
  currency: string,
  locale: string,
  opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): Intl.NumberFormat {
  const key = `${locale}|${currency}|${opts?.minimumFractionDigits ?? ""}|${opts?.maximumFractionDigits ?? ""}`;
  const cached = moneyFormatterCache.get(key);
  if (cached) return cached;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: opts?.minimumFractionDigits,
    maximumFractionDigits: opts?.maximumFractionDigits,
  });
  moneyFormatterCache.set(key, formatter);
  return formatter;
}

/**
 * Format a monetary amount using the shop's country config.
 *
 * @param amount   - The numeric amount (e.g. 49.99)
 * @param options  - Override currency/locale or fraction digits.
 *                   If not provided, defaults to USD / en-US.
 *
 * Usage:
 *   formatMoney(100)                              → "$100.00"
 *   formatMoney(100, { currency: "EUR", locale: "es-ES" }) → "100,00 €"
 *   formatMoney(100, { countryCode: "ES" })       → "100,00 €"
 *   formatMoney(100, { maximumFractionDigits: 0 }) → "$100"
 */
export function formatMoney(
  amount: number,
  options?: {
    currency?: string;
    locale?: string;
    countryCode?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  let currency = options?.currency || "USD";
  let locale = options?.locale || "en-US";

  if (options?.countryCode) {
    const config = getCountryConfig(options.countryCode);
    currency = options?.currency || config.currency;
    locale = options?.locale || config.defaultLocale;
  }

  return getMoneyFormatter(currency, locale, {
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits,
  }).format(amount);
}

/**
 * Shorthand: format money with no decimal places (for summary cards).
 */
export function formatMoneyCompact(
  amount: number,
  options?: { currency?: string; locale?: string; countryCode?: string }
): string {
  return formatMoney(amount, { ...options, minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// ─── Date Formatting ─────────────────────────────────────────────────────────

/**
 * Format a date in the shop's timezone and locale.
 * Wraps Intl.DateTimeFormat for consistent locale handling.
 */
export function formatDate(
  date: Date | string,
  options?: {
    locale?: string;
    timezone?: string;
    dateStyle?: "full" | "long" | "medium" | "short";
    timeStyle?: "full" | "long" | "medium" | "short";
  }
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const locale = options?.locale || "en-US";
  const formatOpts: Intl.DateTimeFormatOptions = {};

  if (options?.timezone) formatOpts.timeZone = options.timezone;
  if (options?.dateStyle) formatOpts.dateStyle = options.dateStyle;
  if (options?.timeStyle) formatOpts.timeStyle = options.timeStyle;

  // Default to medium date if nothing specified
  if (!options?.dateStyle && !options?.timeStyle) {
    formatOpts.dateStyle = "medium";
  }

  return new Intl.DateTimeFormat(locale, formatOpts).format(d);
}

// ─── Number Formatting ───────────────────────────────────────────────────────

/**
 * Format a number with locale-appropriate separators.
 */
export function formatNumber(
  value: number,
  options?: { locale?: string; minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  const locale = options?.locale || "en-US";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits,
  }).format(value);
}
